// @ts-check
/**
 * Related Products (App) - Mix Liquid + JS pagination.
 *
 * First visit (no sessionStorage counter):
 *   Liquid-rendered cards are already correct → remove shimmer to reveal them.
 *   Fetch all 250 products in background → cache in sessionStorage → set counter = 1.
 *
 * Subsequent visits (counter exists):
 *   Load cache, slice by (counter * limit) % total, replace card DOM → remove shimmer → counter++.
 *   Prefetch next 250 when within 2 pages of cache end and hasNextPage is true.
 */

if (!customElements.get('ff-related-products-sf')) {
  class FfRelatedProductsSf extends HTMLElement {
    connectedCallback() {
      this.ffSlider = this.querySelector('ff-slider');
      this.cardClass = this.dataset.cardClass || '';
      this.limit = parseInt(this.dataset.limit || '8', 10);
      this.initWhenReady();
    }

    initWhenReady() {
      // @ts-ignore
      if (this.ffSlider && this.ffSlider.splide) {
        // @ts-ignore
        this.splideInstance = this.ffSlider.splide;
        this.mixLoad();
      } else {
        setTimeout(() => this.initWhenReady(), 100);
      }
    }

    async mixLoad() {
      const config = window.__ffRelatedProductsConfig;
      if (!config?.promise) {
        console.warn('[RelatedRP] window.__ffRelatedProductsConfig not found — hiding block');
        this.hide();
        return;
      }

      const handle = config.collectionHandle;
      const lang = config.language || 'EN';
      const cacheKey = `ff_rp_products_${handle}_${lang}`;
      const counterKey = `ff_rp_counter_${handle}_${lang}`;
      const cursorKey = `ff_rp_cursor_${handle}_${lang}`;

      const counter = sessionStorage.getItem(counterKey);
      const cached = sessionStorage.getItem(cacheKey);

      if (!counter) {
        this.removeShimmer();
        this.show();

        // Background fetch: cache 250 products for next visits
        try {
          const json = await config.promise;
          const result = json?.data?.collection?.products;
          if (result?.nodes?.length) {
            sessionStorage.setItem(cacheKey, JSON.stringify(result.nodes));
            if (result.pageInfo?.endCursor) {
              sessionStorage.setItem(cursorKey, JSON.stringify(result.pageInfo));
            }
          }
          sessionStorage.setItem(counterKey, '1');
        } catch (e) {
          console.warn('[RelatedRP] background fetch failed', e);
        }
        return;
      }

      // SUBSEQUENT VISITS — replace cards from cache
      if (!cached) {
        console.warn('[RelatedRP] Counter exists but cache is missing — resetting to first visit');
        sessionStorage.removeItem(counterKey);
        this.removeShimmer();
        this.show();
        return;
      }

      try {
        const allProducts = JSON.parse(cached);
        const filtered = allProducts.filter(p => {
          if (!config.excludeId) return true;
          return !p.id.endsWith(`/${config.excludeId}`);
        });

        if (!filtered.length) {
          console.warn('[RelatedRP] No products after filtering — hiding block');
          this.hide();
          return;
        }

        const total = filtered.length;
        const { limit } = this;
        const counterNum = parseInt(counter, 10);
        const offset = (counterNum * limit) % total;

        const products = offset + limit <= total
          ? filtered.slice(offset, offset + limit)
          : [...filtered.slice(offset), ...filtered.slice(0, limit - (total - offset))];

        this.replaceSlides(products);
        this.show();
        sessionStorage.setItem(counterKey, String(counterNum + 1));

        // Prefetch next 250 when within 2 pages of end
        const pageInfo = this.getStoredPageInfo(cursorKey);
        if (pageInfo?.hasNextPage && offset + limit * 2 >= total) {
          this.prefetch(handle, cacheKey, cursorKey, pageInfo.endCursor);
        }
      } catch (e) {
        console.warn('[RelatedRP] error replacing cards', e);
        this.removeShimmer();
      }
    }

    /**
     * Updates only the dynamic values within the existing Liquid-rendered card HTML.
     * Clears src, updates texts/href, then sets the real src in rAF before removing the skeleton.
     * @param {Array} products
     */
    replaceSlides(products) {
      const list = this.ffSlider?.querySelector('.splide__list');
      if (!list) return;

      const slides = list.querySelectorAll('.splide__slide:not(.splide__slide--clone)');

      /** @type {Array<{img: HTMLImageElement, url: string, alt: string}>} */
      const imageUpdates = [];

      products.forEach((p, i) => {
        if (!slides[i]) return;
        const slide = slides[i];

        const a = slide.querySelector('a.ff-product-card');
        if (a) a.setAttribute('href', `/products/${p.handle}`);

        const imgPrimary = /** @type {HTMLImageElement|null} */ (slide.querySelector('.ff-product-card__image-wrapper img:not(.ff-product-card__image--secondary)'));
        const imgSecondary = /** @type {HTMLImageElement|null} */ (slide.querySelector('.ff-product-card__image-wrapper img.ff-product-card__image--secondary'));
        const imgPrimaryData = p.images?.nodes?.[0] || p.featuredImage;
        const imgSecondaryData = p.images?.nodes?.[1] || imgPrimaryData;
        if (imgPrimary && imgPrimaryData) {
          imgPrimary.setAttribute('src', '');
          imgPrimary.setAttribute('srcset', '');
          imageUpdates.push({ img: imgPrimary, url: imgPrimaryData.url, alt: this.esc(imgPrimaryData.altText || p.title) });
        }
        if (imgSecondary && imgSecondaryData) {
          imgSecondary.setAttribute('src', '');
          imgSecondary.setAttribute('srcset', '');
          imageUpdates.push({ img: imgSecondary, url: imgSecondaryData.url, alt: this.esc(imgSecondaryData.altText || p.title) });
        }

        const title = slide.querySelector('.ff-product-card__title');
        if (title) title.textContent = this.productTitle(p);

        const vendor = slide.querySelector('.ff-product-card__vendor');
        if (vendor) {
          const colorSlider = slide.querySelector('.ff-product-card__color-slider');
          const hasColorSlider = colorSlider != null && getComputedStyle(colorSlider).display !== 'none';
          vendor.textContent = hasColorSlider ? this.colorLabel(p) : this.esc(p.vendor || '');
        }

        const price = slide.querySelector('.ff-product-card__price');
        const priceRange = p.priceRange?.minVariantPrice;
        if (price && priceRange) {
          price.innerHTML = this.formatMoney(priceRange.amount, priceRange.currencyCode);
        }
      });

      requestAnimationFrame(() => {
        imageUpdates.forEach(({ img, url, alt }) => {
          img.setAttribute('alt', alt);
          img.setAttribute('src', url);
        });
        // @ts-ignore
        this.splideInstance?.refresh();
        // @ts-ignore
        this.splideInstance?.emit('move', 0, 0, 0);
        this.removeShimmer();
      });
    }

    /**
     * Mirrors _ff-product-card luxury title logic:
     * custom_title_chanel_metaobject metaobject → field "title", fallback to product.title.
     * @param {Object} product
     */
    productTitle(product) {
      try {
        const mf = Array.isArray(product.metafields)
          ? product.metafields.find(m => m?.key === 'custom_title_chanel_metaobject')
          : null;
        const luxuryTitle = mf?.reference?.field?.value;
        if (luxuryTitle) return luxuryTitle;
      } catch (_) { /* fall through */ }
      return product.title || '';
    }

    /**
     * Returns "N colores" from the group_color metafield, or falls back to vendor.
     * @param {Object} product
     */
    colorLabel(product) {
      try {
        const mf = Array.isArray(product.metafields)
          ? product.metafields.find(m => m?.key === 'group_color')
          : null;
        if (mf?.value) {
          const arr = JSON.parse(mf.value);
          if (Array.isArray(arr) && arr.length > 0) {
            return `${arr.length} colores`;
          }
        }
      } catch (_) { /* fall through */ }
      return this.esc(product.vendor || '');
    }

    /** @param {string} cursorKey */
    getStoredPageInfo(cursorKey) {
      try {
        const raw = sessionStorage.getItem(cursorKey);
        return raw ? JSON.parse(raw) : null;
      } catch (_) { return null; }
    }

    /**
     * Appends the next 250 products to the cache.
     * @param {string} handle
     * @param {string} cacheKey
     * @param {string} cursorKey
     * @param {string} cursor
     */
    async prefetch(handle, cacheKey, cursorKey, cursor) {
      if (this._prefetching) return;
      this._prefetching = true;
      const lang = window.__ffRelatedProductsConfig?.language || 'EN';
      try {
        const res = await fetch(`https://${Shopify.shop}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': Shopify.storefrontAccessToken
          },
          body: JSON.stringify({
            query: `query($handle:String!,$after:String,$language:LanguageCode!)@inContext(language:$language){collection(handle:$handle){products(first:250,sortKey:COLLECTION_DEFAULT,after:$after){nodes{id title handle vendor priceRange{minVariantPrice{amount currencyCode}}featuredImage{url(transform:{maxWidth:1500})altText}images(first:2){nodes{url(transform:{maxWidth:1500})altText}}metafields(identifiers:[{namespace:"magento",key:"group_color"},{namespace:"custom",key:"custom_title_chanel_metaobject"}]){key value reference{...on Metaobject{field(key:"title"){value}}}}}pageInfo{endCursor hasNextPage}}}}`,
            variables: { handle, after: cursor, language: lang }
          })
        });
        const json = await res.json();
        const result = json?.data?.collection?.products;
        if (result?.nodes?.length) {
          const existing = JSON.parse(sessionStorage.getItem(cacheKey) || '[]');
          const merged = [...existing, ...result.nodes];
          sessionStorage.setItem(cacheKey, JSON.stringify(merged));
          if (result.pageInfo?.endCursor) {
            sessionStorage.setItem(cursorKey, JSON.stringify(result.pageInfo));
          }
        }
      } catch (e) {
        console.warn('[RelatedRP] prefetch error', e);
      } finally {
        this._prefetching = false;
      }
    }

    removeShimmer() {
      this.querySelectorAll('.ff-rp--shimmering').forEach(el => {
        el.classList.remove('ff-rp--shimmering');
      });
    }

    /**
     * @param {string} amount
     * @param {string} currencyCode
     */
    formatMoney(amount, currencyCode) {
      const format = window.Shopify?.money_format || `{{amount}} ${currencyCode}`;
      const cents = Math.round(parseFloat(amount) * 100);
      const formatted = (cents / 100).toFixed(2);
      const withComma = (cents / 100).toFixed(2).replace('.', ',');
      return format
        .replace('{{amount}}', formatted)
        .replace('{{amount_with_comma_separator}}', withComma)
        .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
        .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
    }

    /** @param {string} str */
    esc(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    hide() { this.classList.add('ff-d-none'); }
    show() { this.classList.remove('ff-d-none'); }
  }

  customElements.define('ff-related-products-sf', FfRelatedProductsSf);
}
